// ============================================
// Chat Context Provider
// Manages chat windows, sidebar state, and global chat functionality
// ============================================

import React, { createContext, useContext, useState, useCallback, ReactNode, useRef } from 'react';
import type { ChatWindowState } from '@/types/chat';
import { useOnlineStatus, useChatConversations, useChatUnreadCountFromSource } from '@/hooks/useChat';
import type { DMConversationWithDetails, GroupChatWithDetails } from '@/types/chat';

interface ChatContextType {
  // Chat windows (floating)
  openWindows: ChatWindowState[];
  openChatWindow: (window: Omit<ChatWindowState, 'isOpen' | 'isMinimized'>) => void;
  closeChatWindow: (id: string) => void;
  minimizeChatWindow: (id: string) => void;
  maximizeChatWindow: (id: string) => void;
  
  // Sidebar
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  openSidebar: () => void;
  closeSidebar: () => void;
  closeWithDelay: (delayMs?: number) => void;
  cancelCloseDelay: () => void;
  
  // Global chat state
  unreadCount: number;
  onlineStatus: 'online' | 'offline' | 'away';
  conversations: DMConversationWithDetails[];
  groups: GroupChatWithDetails[];
  anonymousSessions: any[];
  loadingConversations: boolean;
  conversationsError: Error | null;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

interface ChatProviderProps {
  children: ReactNode;
  maxWindows?: number; // Maximum number of chat windows open at once
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children, maxWindows = 3 }) => {
  const [openWindows, setOpenWindows] = useState<ChatWindowState[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  const hasActiveChatWindow = openWindows.some(
    (w) => w.isOpen && !w.isMinimized && (w.type === 'dm' ? Boolean(w.otherUserId) : true)
  );
  const onlineStatus = useOnlineStatus(hasActiveChatWindow);
  const {
    conversations,
    groups,
    anonymousSessions,
    loading: loadingConversations,
    error: conversationsError,
  } = useChatConversations();
  const unreadCount = useChatUnreadCountFromSource({ conversations, groups, anonymousSessions });

  // Log when unreadCount changes to verify reactivity
  React.useEffect(() => {
    console.debug('[ChatContext] unreadCount updated:', unreadCount);
  }, [unreadCount]);

  // Open a chat window
  const openChatWindow = useCallback(
    (window: Omit<ChatWindowState, 'isOpen' | 'isMinimized'>) => {
      setOpenWindows((prev) => {
        // Check if window already exists
        const existingIndex = prev.findIndex((w) => w.id === window.id);
        
        if (existingIndex !== -1) {
          // Window exists: refresh payload and maximize it if minimized
          return prev.map((w, i) =>
            i === existingIndex ? { ...w, ...window, isMinimized: false, isOpen: true } : w
          );
        }

        // Add new window
        const newWindow: ChatWindowState = {
          ...window,
          isOpen: true,
          isMinimized: false,
        };

        // If we're at max windows, close the oldest one
        if (prev.length >= maxWindows) {
          return [...prev.slice(1), newWindow];
        }

        return [...prev, newWindow];
      });
    },
    [maxWindows]
  );

  // Close a chat window
  const closeChatWindow = useCallback((id: string) => {
    setOpenWindows((prev) => prev.filter((w) => w.id !== id));
  }, []);

  // Minimize a chat window
  const minimizeChatWindow = useCallback((id: string) => {
    setOpenWindows((prev) =>
      prev.map((w) => (w.id === id ? { ...w, isMinimized: true } : w))
    );
  }, []);

  // Maximize a chat window
  const maximizeChatWindow = useCallback((id: string) => {
    setOpenWindows((prev) =>
      prev.map((w) => (w.id === id ? { ...w, isMinimized: false } : w))
    );
  }, []);

  // Sidebar controls
  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => {
      if (!prev) {
        window.dispatchEvent(new CustomEvent('sidebar-opened', { detail: { type: 'chat' } }));
      }
      return !prev;
    });
  }, []);

  const openSidebar = useCallback(() => {
    // Forzar recálculo del header inmediatamente antes de abrir
    window.dispatchEvent(new Event('recalculate-header'));
    // Pequeño delay para asegurar que el recálculo se complete
    requestAnimationFrame(() => {
      setSidebarOpen(true);
      window.dispatchEvent(new CustomEvent('sidebar-opened', { detail: { type: 'chat' } }));
    });
  }, []);

  const closeSidebar = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setSidebarOpen(false);
  }, []);

  const cancelCloseDelay = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const closeWithDelay = useCallback((delayMs: number = 1000) => {
    cancelCloseDelay();
    closeTimerRef.current = setTimeout(() => {
      setSidebarOpen(false);
      closeTimerRef.current = null;
    }, delayMs);
  }, [cancelCloseDelay]);

  React.useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  // Listen for other sidebars opening
  React.useEffect(() => {
    const handleSidebarOpened = (event: CustomEvent) => {
      if (event.detail.type !== 'chat') {
        if (closeTimerRef.current) {
          clearTimeout(closeTimerRef.current);
          closeTimerRef.current = null;
        }
        setSidebarOpen(false);
      }
    };
    window.addEventListener('sidebar-opened', handleSidebarOpened as EventListener);
    return () => {
      window.removeEventListener('sidebar-opened', handleSidebarOpened as EventListener);
    };
  }, []);

  // Listen for anonymous chat migration (when user logs in after chatting anonymously)
  React.useEffect(() => {
    const handleAnonymousChatMigrated = (event: CustomEvent) => {
      // Close all anonymous chat windows
      setOpenWindows((prev) => prev.filter((w) => !w.isAnonymous));
    };
    window.addEventListener('anonymous-chat-migrated', handleAnonymousChatMigrated as EventListener);
    return () => {
      window.removeEventListener('anonymous-chat-migrated', handleAnonymousChatMigrated as EventListener);
    };
  }, []);

  return (
    <ChatContext.Provider
      value={{
        openWindows,
        openChatWindow,
        closeChatWindow,
        minimizeChatWindow,
        maximizeChatWindow,
        sidebarOpen,
        toggleSidebar,
        openSidebar,
        closeSidebar,
        closeWithDelay,
        cancelCloseDelay,
        unreadCount,
        onlineStatus,
        conversations,
        groups,
        anonymousSessions,
        loadingConversations,
        conversationsError,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

// Custom hook to use chat context
export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};
